import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import Footer from './Footer';
import UserAvatar from './UserAvatar';
import SessionViewer from './SessionViewer';
import TestCaseSelector from './TestCaseSelector';
import ImageModal from './ImageModal';
import ImageUploader from './ImageUploader';
import SecureMarkdownRenderer from './SecureMarkdownRenderer';
import { 
  getStatusEmoji, 
  formatEmojiText,
  getImageUrl
} from '../utils/helpers';

function TestRunner() {
  const { id } = useParams();
  const [plan, setPlan] = useState(null);
  const [results, setResults] = useState({ results: [] });
  const [loading, setLoading] = useState(true);
  const [currentSession, setCurrentSession] = useState({
    username: '',
    platform: '',
    steps: []
  });

  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');
  const [selectedSession, setSelectedSession] = useState(null);
  const [showSessionViewer, setShowSessionViewer] = useState(false);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedTestCaseIndex, setSelectedTestCaseIndex] = useState(0); // Start with first test case selected
  const [testingStarted, setTestingStarted] = useState(false);
  const [testCaseResult, setTestCaseResult] = useState('');
  const [sessionStartTime, setSessionStartTime] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [showImageModal, setShowImageModal] = useState(false);

  useEffect(() => {
    loadPlan();
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadPlan = async () => {
    try {
      const response = await axios.get(`/api/plan/${id}`);
      setPlan(response.data.plan);
      setResults(response.data.results || { results: [] });
      
      // Don't initialize steps until test case is selected
      
      setLoading(false);
    } catch (error) {
      setMessage('Plan not found');
      setMessageType('error');
      setLoading(false);
    }
  };





  const updateStepResult = (stepId, field, value) => {
    setCurrentSession(prev => ({
      ...prev,
      steps: prev.steps.map(step =>
        step.step_id === stepId ? { ...step, [field]: value } : step
      )
    }));
  };

  const getQuestionStepResult = (stepId) => {
    const stepResult = currentSession.steps.find(s => s.step_id === stepId);
    if (!stepResult || !stepResult.answers || stepResult.answers.length === 0) {
      return 'Not Answered';
    }
    return 'Answered';
  };

  const updateStepAnswer = (stepId, answer, isMulti = false, isEvaluate = false) => {
    setCurrentSession(prev => ({
      ...prev,
      steps: prev.steps.map(step =>
        step.step_id === stepId ? {
          ...step,
          answers: isMulti 
            ? (step.answers || []).includes(answer)
              ? (step.answers || []).filter(a => a !== answer)
              : [...(step.answers || []), answer]
            : isEvaluate
            ? (() => {
                const currentAnswers = step.answers || [];
                const baseAnswer = answer.split(':')[0]; // e.g., "Option 1" from "Option 1:pass"
                const evaluation = answer.split(':')[1]; // e.g., "pass" from "Option 1:pass"
                
                // Remove any existing evaluation for this option
                const filteredAnswers = currentAnswers.filter(a => !a.startsWith(baseAnswer + ':'));
                
                // Add new evaluation if it's not clearing
                if (evaluation) {
                  return [...filteredAnswers, answer];
                } else {
                  return filteredAnswers;
                }
              })()
            : [answer]
        } : step
      )
    }));
  };

  const handleImageUpload = async (stepId, files) => {
    const uploadPromises = [];

    for (let file of files) {
      const formData = new FormData();
      formData.append('image', file);
      uploadPromises.push(
        axios.post(`/api/plan/${id}/upload-image`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        })
      );
    }

    try {
      const responses = await Promise.all(uploadPromises);
      const imagePaths = responses.map(response => response.data.image_path);
      
      setCurrentSession(prev => ({
        ...prev,
        steps: prev.steps.map(step =>
          step.step_id === stepId ? {
            ...step,
            images: [...step.images, ...imagePaths]
          } : step
        )
      }));
    } catch (error) {
      setMessage('Failed to upload images');
      setMessageType('error');
      setTimeout(() => setMessage(''), 3000);
    }
  };



  // downloadPlan function moved to Dashboard component



  const shouldShowStep = (step) => {
    if (!step.condition) return true;
    
    const conditionStep = currentSession.steps.find(s => s.step_id === step.condition.step_id);
    if (!conditionStep) return false;
    
    // Check test result condition
    if (step.condition.equals) {
      return conditionStep.result === step.condition.equals;
    }
    
    // Check question answer condition
    if (step.condition.answer_equals) {
      return conditionStep.answers && conditionStep.answers.includes(step.condition.answer_equals);
    }
    
    return false;
  };

  const isStepValid = (step) => {
    if (step.step_type === 'question' && step.question && step.question.required) {
      const stepResult = currentSession.steps.find(s => s.step_id === step.id);
      const hasAnswers = stepResult && stepResult.answers && stepResult.answers.length > 0;
      
      if (step.question.type === 'evaluate') {
        // For evaluate questions, check if at least one option has been evaluated (pass or fail)
        return hasAnswers && stepResult.answers.some(answer => answer.includes(':pass') || answer.includes(':fail'));
      } else {
        // For single/multi select, check if at least one option is selected
        return hasAnswers;
      }
    }
    
    // For test steps or non-required questions, always valid
    return true;
  };

  const getResultIcon = (result) => {
    return getStatusEmoji(result);
  };

  const handleViewSession = (session) => {
    setSelectedSession(session);
    setShowSessionViewer(true);
  };

  const getStepSessions = (stepId) => {
    return results.results.filter(session => 
      session.test_case_id === plan.test_cases[selectedTestCaseIndex]?.id &&
      session.steps.some(step => step.step_id === stepId)
    );
  };

  // getTestCaseSessions function moved to Dashboard component

  const saveCurrentProgress = async () => {
    if (!currentSession.username || !currentSession.platform || !currentSession.test_case_id || !sessionStartTime) {
      return;
    }

    try {
      const payload = {
        username: currentSession.username,
        platform: currentSession.platform,
        test_case_id: currentSession.test_case_id,
        started_at: sessionStartTime,
        steps: currentSession.steps
      };
      await axios.post(`/api/plan/${id}/update`, payload);
    } catch (error) {
      console.error('Failed to save progress:', error);
    }
  };

  const handleStartTesting = async () => {
    if (selectedTestCaseIndex === null || !currentSession.username || !currentSession.platform) {
      setMessage('Please enter username, platform, and select a test case');
      setMessageType('error');
      setTimeout(() => setMessage(''), 3000);
      return;
    }

    const selectedTestCase = plan.test_cases[selectedTestCaseIndex];
    const sessionSteps = selectedTestCase.steps.map(step => ({
      step_id: step.id,
      result: step.step_type === 'question' ? 'Answered' : step.step_type === 'instruction' ? 'Info' : 'Untested',
      notes: '',
      images: [],
      answers: step.step_type === 'question' ? [] : undefined
    }));
    
    const startTime = new Date().toISOString();
    setSessionStartTime(startTime);
    
    // Start the session on the backend
    try {
      const sessionData = {
        username: currentSession.username,
        platform: currentSession.platform,
        test_case_id: selectedTestCase.id,
        test_case_result: 'Testing',
        steps: sessionSteps
      };
      
      await axios.post(`/api/plan/${id}/start`, sessionData);
      
      setCurrentSession(prev => ({ 
        ...prev, 
        test_case_id: selectedTestCase.id,
        steps: sessionSteps,
        started_at: startTime
      }));
      setTestingStarted(true);
      setCurrentStepIndex(0);
      setTestCaseResult('');
      
      // Smooth scroll to top of test case container when starting test
      setTimeout(() => {
        const testCaseContainer = document.getElementById('test-case-container');
        if (testCaseContainer) {
          testCaseContainer.scrollIntoView({
            behavior: 'smooth',
            block: 'start'
          });
        } else {
          // Fallback to page top if container not found
          window.scrollTo({
            top: 0,
            behavior: 'smooth'
          });
        }
      }, 100);
      
      // Reload to get updated results
      loadPlan();
    } catch (error) {
      setMessage('Failed to start test session');
      setMessageType('error');
      setTimeout(() => setMessage(''), 3000);
    }
  };

  const handleNextStep = async () => {
    const visibleSteps = plan.test_cases[selectedTestCaseIndex].steps.filter(shouldShowStep);
    
    // Only validate if we're still on a regular step (not the overall result step)
    if (currentStepIndex < visibleSteps.length) {
      const currentStep = visibleSteps[currentStepIndex];
      
      // Validate current step if it's required
      if (!isStepValid(currentStep)) {
        setMessage('Please complete all required fields before proceeding');
        setMessageType('error');
        setTimeout(() => setMessage(''), 3000);
        return;
      }
      
      // Save current progress
      await saveCurrentProgress();
    }
    
    // Move to next step (including the overall result step)
    if (currentStepIndex < visibleSteps.length) {
      setCurrentStepIndex(currentStepIndex + 1);
      
      // Smooth scroll to top of test case container
      setTimeout(() => {
        const testCaseContainer = document.getElementById('test-case-container');
        if (testCaseContainer) {
          testCaseContainer.scrollIntoView({
            behavior: 'smooth',
            block: 'start'
          });
        } else {
          // Fallback to page top if container not found
          window.scrollTo({
            top: 0,
            behavior: 'smooth'
          });
        }
      }, 100); // Small delay to ensure step has rendered
    }
  };

  const handlePreviousStep = () => {
    if (currentStepIndex > 0) {
      setCurrentStepIndex(currentStepIndex - 1);
      
      // Smooth scroll to top of test case container
      setTimeout(() => {
        const testCaseContainer = document.getElementById('test-case-container');
        if (testCaseContainer) {
          testCaseContainer.scrollIntoView({
            behavior: 'smooth',
            block: 'start'
          });
        } else {
          // Fallback to page top if container not found
          window.scrollTo({
            top: 0,
            behavior: 'smooth'
          });
        }
      }, 100); // Small delay to ensure step has rendered
    }
  };

  const handleSubmitFinal = async () => {
    if (!currentSession.username || !currentSession.platform || !sessionStartTime) {
      setMessage('Please enter username and platform');
      setMessageType('error');
      setTimeout(() => setMessage(''), 3000);
      return;
    }

    // Validate overall test case result is selected
    if (!testCaseResult || testCaseResult === '') {
      setMessage('Please select an overall test case result before submitting');
      setMessageType('error');
      setTimeout(() => setMessage(''), 3000);
      return;
    }

    // Validate all required steps
    const visibleSteps = plan.test_cases[selectedTestCaseIndex].steps.filter(shouldShowStep);
    const invalidSteps = visibleSteps.filter(step => !isStepValid(step));
    
    if (invalidSteps.length > 0) {
      const stepTitles = invalidSteps.map(step => step.title).join(', ');
      setMessage(`Please complete all required fields in the following steps: ${stepTitles}`);
      setMessageType('error');
      setTimeout(() => setMessage(''), 5000);
      return;
    }

    setIsSubmitting(true);
    try {
      // First update with final steps
      await saveCurrentProgress();
      
      // Then complete the session
      const completeData = {
        username: currentSession.username,
        platform: currentSession.platform,
        test_case_id: currentSession.test_case_id,
        started_at: sessionStartTime,
        test_case_result: testCaseResult
      };
      
      await axios.post(`/api/plan/${id}/complete`, completeData);
      
      setMessage('Test results submitted successfully!');
      setMessageType('success');
      loadPlan(); // Reload to show updated results
      setCurrentStepIndex(0); // Reset to first step
      setTestingStarted(false); // Return to test case selection
      setSelectedTestCaseIndex(0); // Reset to first test case
      setTestCaseResult(''); // Reset test case result
      setSessionStartTime(null); // Reset session start time
      
      // Smooth scroll to top of test case container after submission
      setTimeout(() => {
        const testCaseContainer = document.getElementById('test-case-container');
        if (testCaseContainer) {
          testCaseContainer.scrollIntoView({
            behavior: 'smooth',
            block: 'start'
          });
        } else {
          // Fallback to page top if container not found
          window.scrollTo({
            top: 0,
            behavior: 'smooth'
          });
        }
      }, 100);
    } catch (error) {
      setMessage('Failed to submit results');
      setMessageType('error');
      setTimeout(() => setMessage(''), 3000);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Auto-save progress every 30 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      if (currentSession.username && currentSession.platform) {
        saveCurrentProgress();
      }
    }, 30000);

    return () => clearInterval(interval);
  }, [currentSession, id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-refresh results every 10 seconds to see live updates
  useEffect(() => {
    const interval = setInterval(() => {
      loadPlan();
    }, 10000);

    return () => clearInterval(interval);
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) {
    return (
      <div className="container">
        <div className="loading">
          <div className="spinner"></div>
        </div>
      </div>
    );
  }

  if (!plan) {
    return (
      <div className="container">
        <div className="alert alert-error">
          Plan not found. Please check the URL and try again.
        </div>
        <Link to="/" className="btn btn-primary">← Back to Home</Link>
      </div>
    );
  }

  return (
    <div className="container">
      <div className="header">
        <h1>{formatEmojiText('🧪', plan.title)}</h1>
        <SecureMarkdownRenderer>{plan.description}</SecureMarkdownRenderer>
      </div>

      <div style={{ marginBottom: '2rem' }}>
        <Link to="/" className="btn btn-secondary" style={{ marginRight: '1rem' }}>
          ← Back to Home
        </Link>
        <Link to={`/dashboard/${id}`} className="btn btn-primary">
          {formatEmojiText('📊', 'View Dashboard')}
        </Link>
      </div>

      {message && (
        <div className={`alert alert-${messageType}`}>
          {message}
        </div>
      )}

            <div className="card">
        <h2>{formatEmojiText('👤', 'Test Session')}</h2>
        <div className="form-group">
          <label>Your Name</label>
          <input
            type="text"
            className="form-control"
            value={currentSession.username}
            onChange={(e) => setCurrentSession(prev => ({ ...prev, username: e.target.value }))}
            placeholder="Enter your name"
          />
        </div>
        <div className="form-group">
          <label>Platform</label>
          <select
            className="form-control"
            value={currentSession.platform}
            onChange={(e) => setCurrentSession(prev => ({ ...prev, platform: e.target.value }))}
          >
            <option value="">Select platform</option>
            {plan.platforms.map(platform => (
              <option key={platform} value={platform}>{platform}</option>
            ))}
          </select>
        </div>
        <div style={{ fontSize: '0.875rem', color: '#6b7280', fontStyle: 'italic' }}>
          Progress auto-saves every 30 seconds
        </div>
      </div>

      {!testingStarted ? (
        <TestCaseSelector
          testCases={plan.test_cases}
          selectedTestCase={selectedTestCaseIndex}
          onSelect={setSelectedTestCaseIndex}
          onStart={handleStartTesting}
          results={results}
          onViewSession={handleViewSession}
          planId={id}
        />
      ) : (
        <>
          <div className="card">
            <h2>{formatEmojiText('📋', 'Test Summary')}</h2>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: '1rem' }}>
              {(() => {
                const visibleSteps = plan.test_cases[selectedTestCaseIndex].steps.filter(shouldShowStep);
                const testSteps = visibleSteps.filter(step => step.step_type !== 'question' && step.step_type !== 'instruction');
                const questionSteps = visibleSteps.filter(step => step.step_type === 'question');
                
                const testCounts = ['Pass', 'Fail', 'Skip', 'Testing', 'Untested'].map(status => ({
                  status,
                  count: testSteps.filter(step => {
                    const stepResult = currentSession.steps.find(s => s.step_id === step.id);
                    return stepResult ? stepResult.result === status : status === 'Untested';
                  }).length,
                  icon: getResultIcon(status)
                }));
                
                const questionCounts = [
                  {
                    status: 'Answered',
                    count: questionSteps.filter(step => getQuestionStepResult(step.id) === 'Answered').length,
                    icon: '✅'
                  },
                  {
                    status: 'Not Answered',
                    count: questionSteps.filter(step => getQuestionStepResult(step.id) === 'Not Answered').length,
                    icon: '❓'
                  }
                ];
                
                return [...testCounts, ...questionCounts].filter(item => item.count > 0).map(({ status, count, icon }) => (
                  <div key={status} style={{ 
                textAlign: 'center', 
                padding: '1rem', 
                border: `1px solid ${window.matchMedia('(prefers-color-scheme: dark)').matches ? '#4b5563' : '#e2e8f0'}`, 
                borderRadius: '8px',
                backgroundColor: window.matchMedia('(prefers-color-scheme: dark)').matches ? '#374151' : '#ffffff'
              }}>
                    <div style={{ fontSize: '1.5rem', marginBottom: '0.5rem' }}>
                      {icon}
                    </div>
                    <div style={{ fontSize: '1.2rem', fontWeight: 'bold' }}>{count}</div>
                    <div style={{ fontSize: '0.9rem', color: '#6b7280' }}>{status}</div>
                  </div>
                ));
              })()}
            </div>
          </div>

          {/* Test Case Information */}
          <div id="test-case-container" className="card">
            <h2>{formatEmojiText('📋', `Test Case: ${plan.test_cases[selectedTestCaseIndex].title}`)}</h2>
            {plan.test_cases[selectedTestCaseIndex].description && (
              <div style={{ marginBottom: '1rem' }}>
                <SecureMarkdownRenderer>{plan.test_cases[selectedTestCaseIndex].description}</SecureMarkdownRenderer>
              </div>
            )}
            
            {plan.test_cases[selectedTestCaseIndex].design_link && (
              <div style={{ marginBottom: '1rem' }}>
                <a 
                  href={plan.test_cases[selectedTestCaseIndex].design_link} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '0.25rem',
                    padding: '0.5rem 1rem',
                    backgroundColor: '#8b5cf6',
                    color: 'white',
                    borderRadius: '16px',
                    textDecoration: 'none',
                    fontSize: '0.875rem',
                    fontWeight: '500',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#7c3aed';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = '#8b5cf6';
                  }}
                >
                  🎨 View Test Case Design
                </a>
              </div>
            )}

            {plan.test_cases[selectedTestCaseIndex].ui_images && plan.test_cases[selectedTestCaseIndex].ui_images.length > 0 && (
              <div style={{ marginBottom: '1rem' }}>
                <h4>Test Case Reference Images</h4>
                <div className="image-preview">
                  {plan.test_cases[selectedTestCaseIndex].ui_images.map((image, imgIndex) => (
                    <img 
                      key={imgIndex} 
                      src={getImageUrl(image, id)} 
                      alt={`Test case reference ${imgIndex + 1}`}
                      style={{ cursor: 'pointer' }}
                      onClick={() => {
                        setSelectedImage(image);
                        setShowImageModal(true);
                      }}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
          <h2>{formatEmojiText('📝', 'Test Steps')}</h2>
          <div className="step-counter">
            Step {currentStepIndex + 1} of {plan.test_cases[selectedTestCaseIndex].steps.filter(shouldShowStep).length + 1}
          </div>
        </div>
        
        {(() => {
          const visibleSteps = plan.test_cases[selectedTestCaseIndex].steps.filter(shouldShowStep);
          
          // Show regular steps
          if (currentStepIndex < visibleSteps.length) {
            const step = visibleSteps[currentStepIndex];
            return (
            <div key={step.id} className="step-item" style={{ marginBottom: '2rem' }}>
                        <div className="step-header" style={{ 
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              padding: '1.5rem',
              marginBottom: '0',
              boxShadow: '0 4px 15px rgba(0, 0, 0, 0.1)'
            }}>
               <div style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                <div>
                  <h3 style={{ margin: 0, fontSize: '1.5rem', fontWeight: '600' }}>
                    {step.step_type === 'question' ? '❓' : step.step_type === 'instruction' ? '📖' : '🧪'} {step.title}
                  </h3>
                  <div style={{ marginTop: '0.5rem', fontSize: '0.875rem', opacity: '0.9' }}>
                    {step.step_type === 'question' ? 'Question Step' : step.step_type === 'instruction' ? 'Instruction Step' : 'Test Step'}
                  </div>
                </div>
                {step.step_type !== 'instruction' && (
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '0.75rem',
                    padding: '0.75rem 1rem',
                    backgroundColor: 'rgba(255, 255, 255, 0.2)',
                    borderRadius: '8px',
                    backdropFilter: 'blur(10px)',
                    marginLeft: 'auto'
                  }}>
                    {step.step_type === 'question' ? (
                      <>
                        <span style={{ fontSize: '1.2rem' }}>
                          {getQuestionStepResult(step.id) === 'Answered' ? '✅' : '❓'}
                        </span>
                        <span style={{ fontWeight: '500' }}>
                          {getQuestionStepResult(step.id)}
                        </span>
                      </>
                    ) : (
                      <>
                        <span style={{ fontSize: '1.2rem' }}>
                          {getResultIcon(currentSession.steps.find(s => s.step_id === step.id)?.result || 'Untested')}
                        </span>
                        <span style={{ fontWeight: '500' }}>
                          {currentSession.steps.find(s => s.step_id === step.id)?.result || 'Untested'}
                        </span>
                      </>
                    )}
                  </div>
                )}
              </div>
            </div>
            
            <div className="step-content" style={{
              border: `1px solid ${window.matchMedia('(prefers-color-scheme: dark)').matches ? '#374151' : '#e2e8f0'}`,
              borderTop: 'none',
              padding: '1.5rem',
              backgroundColor: window.matchMedia('(prefers-color-scheme: dark)').matches ? '#1f2937' : 'white',
              boxShadow: '0 4px 15px rgba(0, 0, 0, 0.05)'
            }}>
                {step.description && (
                  <div style={{ marginBottom: '1.5rem' }}>
                    <h4 style={{ 
                      color: window.matchMedia('(prefers-color-scheme: dark)').matches ? '#e5e7eb' : '#374151', 
                      marginBottom: '0.75rem', 
                      fontSize: '1rem',
                      fontWeight: '600',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem'
                    }}>
                      📝 Step Description
                    </h4>
                    <div style={{
                      padding: '1rem',
                      backgroundColor: window.matchMedia('(prefers-color-scheme: dark)').matches ? '#374151' : '#f8fafc',
                      borderRadius: '8px',
                      border: `1px solid ${window.matchMedia('(prefers-color-scheme: dark)').matches ? '#4b5563' : '#e2e8f0'}`
                    }}>
                      <SecureMarkdownRenderer>{step.description}</SecureMarkdownRenderer>
                    </div>
                  </div>
                )}
                
                {step.design_link && (
                  <div style={{ marginBottom: '1rem' }}>
                    <a 
                      href={step.design_link} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '0.25rem',
                        padding: '0.25rem 0.75rem',
                        backgroundColor: '#8b5cf6',
                        color: 'white',
                        borderRadius: '16px',
                        textDecoration: 'none',
                        fontSize: '0.875rem',
                        fontWeight: '500',
                        transition: 'all 0.2s ease'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = '#7c3aed';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = '#8b5cf6';
                      }}
                    >
                      🎨 View Design
                    </a>
                  </div>
                )}

                                 {step.ui_images && step.ui_images.length > 0 && (
                   <div style={{ marginBottom: '1rem' }}>
                     <h4>Reference Images</h4>
                     <div className="image-preview">
                       {step.ui_images.map((image, imgIndex) => (
                         <img 
                           key={imgIndex} 
                           src={getImageUrl(image, id)} 
                           alt={`UI reference ${imgIndex + 1}`}
                           style={{ cursor: 'pointer' }}
                           onClick={() => {
                             setSelectedImage(image);
                             setShowImageModal(true);
                           }}
                         />
                       ))}
                     </div>
                   </div>
                 )}

                 {step.step_type !== 'instruction' && getStepSessions(step.id).filter(session => {
                   const stepResult = session.steps.find(s => s.step_id === step.id);
                   return stepResult && stepResult.result !== 'Untested';
                 }).length > 0 && (
                   <div>
                     <h4>Other Test Results</h4>
                     <div className="user-avatars">
                       {getStepSessions(step.id)
                         .filter(session => {
                           const stepResult = session.steps.find(s => s.step_id === step.id);
                           return stepResult && stepResult.result !== 'Untested';
                         })
                         .map((session, sessionIndex) => (
                         <UserAvatar
                           key={sessionIndex}
                           session={session}
                           stepId={step.id}
                           planId={id}
                           onViewSession={handleViewSession}
                         />
                       ))}
                     </div>
                   </div>
                 )}

                {step.step_type === 'question' && step.question && (
                  <div className="form-group">
                    {/* Question Images */}
                    {step.question.images && step.question.images.length > 0 && (
                      <div style={{ marginBottom: '1rem' }}>
                        <h4>Question Images</h4>
                        <div className="image-preview">
                          {step.question.images.map((image, imgIndex) => (
                            <img 
                              key={imgIndex} 
                              src={getImageUrl(image, id)} 
                                                                   alt={`Question ${imgIndex + 1}`}
                              style={{ cursor: 'pointer' }}
                              onClick={() => {
                                setSelectedImage(image);
                                setShowImageModal(true);
                              }}
                            />
                          ))}
                        </div>
                      </div>
                    )}
                    
                    <label className="question-label">
                      {step.question.type === 'single' && 'Select one:'}
                      {step.question.type === 'multi' && 'Select all that apply:'}
                      {step.question.type === 'evaluate' && 'Evaluate each item:'}
                      {step.question.required && <span style={{ color: '#ef4444', marginLeft: '0.25rem' }}>*</span>}
                    </label>
                    
                    {step.question.type === 'evaluate' ? (
                      <div>
                        {step.question.options.filter(opt => opt.trim() !== '').map((option, optIndex) => {
                          const currentAnswers = currentSession.steps.find(s => s.step_id === step.id)?.answers || [];
                          const passAnswer = `${option}:pass`;
                          const failAnswer = `${option}:fail`;
                          const isPass = currentAnswers.includes(passAnswer);
                          const isFail = currentAnswers.includes(failAnswer);
                          
                          return (
                            <div key={optIndex} className="evaluation-option">
                              <div className="evaluation-button">
                                <div style={{ flex: 1 }}>
                                  <span className="evaluation-text">{option}</span>
                                  {step.question.option_images?.[option] && step.question.option_images[option].length > 0 && (
                                    <div className="image-preview" style={{ marginTop: '0.5rem' }}>
                                      {step.question.option_images[option].map((image, imgIndex) => (
                                        <img 
                                          key={imgIndex} 
                                          src={getImageUrl(image, id)} 
                                          alt={`${option} ${imgIndex + 1}`}
                                          style={{ cursor: 'pointer', maxHeight: '80px' }}
                                          onClick={() => {
                                            setSelectedImage(image);
                                            setShowImageModal(true);
                                          }}
                                        />
                                      ))}
                                    </div>
                                  )}
                                </div>
                                <div className="evaluation-controls">
                                  <div 
                                    className={`eval-btn pass ${isPass ? 'active' : ''}`}
                                    onClick={() => {
                                      if (isPass) {
                                        updateStepAnswer(step.id, `${option}:`, false, true); // Clear
                                      } else {
                                        updateStepAnswer(step.id, passAnswer, false, true);
                                      }
                                    }}
                                  >
                                    ✓
                                  </div>
                                  <div 
                                    className={`eval-btn fail ${isFail ? 'active' : ''}`}
                                    onClick={() => {
                                      if (isFail) {
                                        updateStepAnswer(step.id, `${option}:`, false, true); // Clear
                                      } else {
                                        updateStepAnswer(step.id, failAnswer, false, true);
                                      }
                                    }}
                                  >
                                    ✗
                                  </div>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div style={{ 
                        display: step.question.type === 'single' ? 'grid' : 'block',
                        gridTemplateColumns: step.question.type === 'single' ? 'repeat(auto-fit, minmax(300px, 1fr))' : 'none',
                        gap: step.question.type === 'single' ? '0.75rem' : '0'
                      }}>
                        {step.question.options.filter(opt => opt.trim() !== '').map((option, optIndex) => {
                          const currentAnswers = currentSession.steps.find(s => s.step_id === step.id)?.answers || [];
                          const isSelected = currentAnswers.includes(option);
                          
                          return (
                            <div key={optIndex} className="question-option">
                              <div 
                                className={`${step.question.type === 'single' ? 'custom-radio' : 'custom-checkbox'} ${isSelected ? 'selected' : ''}`}
                                onClick={() => updateStepAnswer(step.id, option, step.question.type === 'multi')}
                              >
                                <input
                                  type={step.question.type === 'multi' ? 'checkbox' : 'radio'}
                                  name={`question-${step.id}`}
                                  checked={isSelected}
                                  readOnly
                                />
                                <div className={step.question.type === 'single' ? 'radio-button' : 'checkbox-button'}></div>
                                <div style={{ flex: 1 }}>
                                  <span className="option-text">{option}</span>
                                  {step.question.option_images?.[option] && step.question.option_images[option].length > 0 && (
                                    <div className="image-preview" style={{ marginTop: '0.5rem' }}>
                                      {step.question.option_images[option].map((image, imgIndex) => (
                                                                                  <img 
                                            key={imgIndex} 
                                            src={getImageUrl(image, id)} 
                                            alt={`${option} ${imgIndex + 1}`}
                                            style={{ cursor: 'pointer', maxHeight: '80px' }}
                                            onClick={(e) => {
                                              e.stopPropagation(); // Prevent option selection when clicking image
                                              setSelectedImage(image);
                                              setShowImageModal(true);
                                            }}
                                          />
                                      ))}
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}

                {step.step_type !== 'question' && step.step_type !== 'instruction' && (
                  <div className="form-group">
                    <label>Test Result</label>
                  <select
                    className="form-control"
                    value={(() => {
                      const stepResult = currentSession.steps.find(s => s.step_id === step.id);
                      if (!stepResult) return 'Untested';
                      
                      return stepResult.result;
                    })()}
                    onChange={(e) => updateStepResult(step.id, 'result', e.target.value)}
                  >
                    <option value="Untested">Untested</option>
                    <option value="Testing">Testing</option>
                    <option value="Pass">Pass</option>
                    <option value="Fail">Fail</option>
                    <option value="Skip">Skip</option>
                  </select>
                </div>
                )}

                <div className="form-group">
                  <label>Notes</label>
                  <textarea
                    className="form-control"
                    value={currentSession.steps.find(s => s.step_id === step.id)?.notes || ''}
                    onChange={(e) => updateStepResult(step.id, 'notes', e.target.value)}
                    placeholder="Add notes about this step..."
                    rows="3"
                  />
                </div>

                {step.step_type !== 'question' && step.step_type !== 'instruction' && (
                  <div className="form-group">
                    <label>Screenshots</label>
                    <ImageUploader
                      onImageUpload={(files) => handleImageUpload(step.id, files)}
                      planId={id}
                      label="📸 Upload test screenshots"
                      images={currentSession.steps.find(s => s.step_id === step.id)?.images || []}
                      onImageClick={(image) => {
                        setSelectedImage(image);
                        setShowImageModal(true);
                      }}
                    />
                  </div>
                )}
                             </div>
           </div>
         );
          } else {
            // Show overall test case result as final step
            return (
              <div key="overall-result" className="step-item" style={{ marginBottom: '2rem' }}>
                                <div className="step-header" style={{ 
                  background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                  color: 'white',
                  padding: '1.5rem',
                  marginBottom: '0',
                  boxShadow: '0 4px 15px rgba(0, 0, 0, 0.1)'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                    <div>
                      <h3 style={{ margin: 0, fontSize: '1.5rem', fontWeight: '600' }}>
                        🏁 Overall Test Case Result
                      </h3>
                      <div style={{ marginTop: '0.5rem', fontSize: '0.875rem', opacity: '0.9' }}>
                        Final Step - Select your overall result
                      </div>
                    </div>
                    <div style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: '0.75rem',
                      padding: '0.75rem 1rem',
                      backgroundColor: 'rgba(255, 255, 255, 0.2)',
                      borderRadius: '8px',
                      backdropFilter: 'blur(10px)',
                      marginLeft: 'auto'
                    }}>
                      <span style={{ fontSize: '1.2rem' }}>
                        {testCaseResult ? getResultIcon(testCaseResult) : '❓'}
                      </span>
                      <span style={{ fontWeight: '500' }}>
                        {testCaseResult || 'Not Selected'}
                      </span>
                    </div>
                  </div>
                </div>
                
                <div className="step-content" style={{
                  border: `1px solid ${window.matchMedia('(prefers-color-scheme: dark)').matches ? '#374151' : '#e2e8f0'}`,
                  borderTop: 'none',
                  padding: '1.5rem',
                  backgroundColor: window.matchMedia('(prefers-color-scheme: dark)').matches ? '#1f2937' : 'white',
                  boxShadow: '0 4px 15px rgba(0, 0, 0, 0.05)'
                }}>
                  <div className="form-group">
                    <label className="question-label">
                      How did this test case go overall?
                      <span style={{ color: '#ef4444', marginLeft: '0.25rem' }}>*</span>
                    </label>
                    
                    <div style={{ 
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                      gap: '0.75rem',
                      marginTop: '1rem'
                    }}>
                      {['Pass', 'Fail', 'Skip'].map((result) => (
                        <div key={result} className="question-option">
                          <div 
                            className={`custom-radio ${testCaseResult === result ? 'selected' : ''}`}
                            onClick={() => setTestCaseResult(result)}
                          >
                            <input
                              type="radio"
                              name="overall-result"
                              checked={testCaseResult === result}
                              readOnly
                            />
                            <div className="radio-button"></div>
                            <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                              <span style={{ fontSize: '1.2rem' }}>{getResultIcon(result)}</span>
                              <span className="option-text" style={{ fontSize: '1rem', fontWeight: '500' }}>
                                {result}
                              </span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            );
          }
        })()}
        
        <div className="navigation-buttons">
          <div>
            {currentStepIndex > 0 && (
              <button className="btn btn-secondary" onClick={handlePreviousStep}>
                ← Previous Step
              </button>
            )}
          </div>
          <div>
            {currentStepIndex < plan.test_cases[selectedTestCaseIndex].steps.filter(shouldShowStep).length ? (
              <button 
                className={`btn ${currentStepIndex < plan.test_cases[selectedTestCaseIndex].steps.filter(shouldShowStep).length ? (isStepValid(plan.test_cases[selectedTestCaseIndex].steps.filter(shouldShowStep)[currentStepIndex]) ? 'btn-primary' : 'btn-secondary') : 'btn-primary'}`}
                onClick={handleNextStep}
                title={currentStepIndex < plan.test_cases[selectedTestCaseIndex].steps.filter(shouldShowStep).length && !isStepValid(plan.test_cases[selectedTestCaseIndex].steps.filter(shouldShowStep)[currentStepIndex]) ? 'Please complete all required fields' : ''}
              >
                Next Step →
              </button>
            ) : (
              <button 
                className={`btn ${!testCaseResult ? 'btn-secondary' : 'btn-success'}`}
                onClick={handleSubmitFinal}
                disabled={isSubmitting || !testCaseResult}
                style={{ 
                  padding: '1rem 2rem', 
                  fontSize: '1.1rem',
                  minWidth: '200px'
                }}
                title={!testCaseResult ? 'Please select an overall test case result first' : ''}
              >
                {isSubmitting ? 'Submitting...' : formatEmojiText('✅', 'Submit Test Results')}
              </button>
            )}
          </div>
        </div>
      </div>
        </>
      )}

      <SessionViewer
        session={selectedSession}
        plan={plan}
        isOpen={showSessionViewer}
        onClose={() => {
          setShowSessionViewer(false);
          setSelectedSession(null);
        }}
      />

      <ImageModal
        isOpen={showImageModal}
        onClose={() => {
          setShowImageModal(false);
          setSelectedImage(null);
        }}
        imageSrc={getImageUrl(selectedImage, id)}
        imageAlt="Test"
      />

      <Footer />
    </div>
  );
}

export default TestRunner; 